import { useCallback, useEffect, useState } from "react";
import "bootstrap-icons/font/bootstrap-icons.css";

import { getPayments } from "../../apis/payments";
import { getPlans } from "../../apis/plans";
import { useCursorPaginatedList, mergeById } from "../../hooks/useCursorPaginatedList";
import {
    formatPaymentAmount,
    formatPaymentDate,
    getPaymentStatusStyle,
    normalizePaymentsResponse,
} from "../../utils/normalizePayment";
import EmptyListState from "../shared/EmptyListState";
import ListScrollFooter from "../shared/ListScrollFooter";
import PageHeader, { PageShell } from "../shared/PageHeader";
import PaymentListFilters from "./PaymentListFilters";
import PaymentListSkeletons from "./PaymentListSkeletons";
import { APP_ROUTES } from "../../constants/appRoutes";

const LOAD_MORE_SKELETON_COUNT = 3;

function getPaymentItemId(payment) {
    return payment.payment_id;
}

function PaymentListCard({ payment }) {
    const statusStyle = getPaymentStatusStyle(payment.status);
    const createdLabel = formatPaymentDate(payment.created_at);
    const amountLabel = formatPaymentAmount(payment);

    return (
        <article className="rounded-xl border border-slate-200 bg-white p-4 transition hover:border-slate-300 md:p-5">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0 flex-1">
                    <div className="mb-2 flex flex-wrap items-center gap-2">
                        <h3 className="mb-0 text-base font-semibold text-slate-900">
                            {payment.plan_name}
                        </h3>
                        {payment.status ? (
                            <span
                                className={`rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${statusStyle}`}
                            >
                                {payment.status}
                            </span>
                        ) : null}
                    </div>
                    <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500 md:text-sm">
                        <span className="inline-flex items-center gap-1">
                            <i className="bi bi-calendar3" aria-hidden />
                            {createdLabel}
                        </span>
                        {payment.payment_id ? (
                            <>
                                <span className="text-slate-300" aria-hidden>
                                    ·
                                </span>
                                <span className="font-mono text-xs text-slate-500">
                                    {payment.payment_id}
                                </span>
                            </>
                        ) : null}
                    </div>
                </div>

                <div className="flex shrink-0 flex-col items-start gap-2 sm:items-end">
                    <p className="mb-0 text-lg font-bold text-slate-900">{amountLabel}</p>
                    {payment.invoice_url ? (
                        <a
                            href={payment.invoice_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-sm font-medium text-slate-700 no-underline hover:text-slate-900"
                        >
                            <i className="bi bi-download" aria-hidden />
                            Invoice
                        </a>
                    ) : null}
                </div>
            </div>
        </article>
    );
}

export default function PaymentsList() {
    const [statusFilter, setStatusFilter] = useState("");
    const [planIdFilter, setPlanIdFilter] = useState("");
    const [planOptions, setPlanOptions] = useState([]);

    useEffect(() => {
        let cancelled = false;

        (async () => {
            try {
                const data = await getPlans();
                const list = (data?.plans ?? [])
                    .filter((plan) => plan.is_active !== false)
                    .map((plan) => ({
                        plan_id: plan.plan_id ?? plan.planId ?? plan.id,
                        name: plan.name ?? "Plan",
                    }))
                    .filter((plan) => plan.plan_id);

                if (!cancelled) setPlanOptions(list);
            } catch {
                if (!cancelled) setPlanOptions([]);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, []);

    const fetchPayments = useCallback(
        (params) => {
            const query = {
                limit: params.limit,
                from_date: params.start_date || undefined,
                to_date: params.end_date || undefined,
                status: statusFilter || undefined,
                plan_id: planIdFilter || undefined,
                last_doc_id: params.last_doc_id || undefined,
            };

            return getPayments(
                Object.fromEntries(
                    Object.entries(query).filter(([, value]) => value != null && value !== "")
                )
            );
        },
        [statusFilter, planIdFilter]
    );

    const {
        items: payments,
        hasMore,
        loading,
        loadingMore,
        error,
        loadMoreRef,
        limit,
        setLimit,
        startDate,
        setStartDate,
        endDate,
        setEndDate,
    } = useCursorPaginatedList({
        fetchFn: fetchPayments,
        normalizeResponse: normalizePaymentsResponse,
        mergeItems: mergeById,
        getItemId: getPaymentItemId,
    });

    const hasActiveFilters = Boolean(statusFilter || planIdFilter || startDate || endDate);

    return (
        <PageShell>
            <PageHeader
                title="Payments"
                description="View your payment history and download invoices."
            />

            <PaymentListFilters
                limit={limit}
                onLimitChange={setLimit}
                startDate={startDate}
                onStartDateChange={setStartDate}
                endDate={endDate}
                onEndDateChange={setEndDate}
                status={statusFilter}
                onStatusChange={setStatusFilter}
                planId={planIdFilter}
                onPlanIdChange={setPlanIdFilter}
                planOptions={planOptions}
            />

            {error ? (
                <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-4 text-red-700">
                    <p className="mb-0 font-semibold">Could not load payments</p>
                    <p className="mt-1 text-sm">{error}</p>
                </div>
            ) : null}

            {loading ? (
                <PaymentListSkeletons count={limit} />
            ) : payments.length === 0 ? (
                <EmptyListState
                    message={
                        hasActiveFilters
                            ? "No payments match your filters."
                            : "No payments to show yet."
                    }
                    actionLabel={!hasActiveFilters ? "View plans" : undefined}
                    actionTo={APP_ROUTES.PLANS}
                />
            ) : (
                <div className="min-w-0 w-full space-y-3">
                    {payments.map((payment, index) => (
                        <PaymentListCard
                            key={payment.payment_id ?? index}
                            payment={payment}
                        />
                    ))}
                </div>
            )}

            {loadingMore ? (
                <PaymentListSkeletons
                    count={LOAD_MORE_SKELETON_COUNT}
                    label="Loading more payments"
                    className="mt-3"
                />
            ) : null}

            <ListScrollFooter
                loadMoreRef={loadMoreRef}
                showAllShown={!loading && !loadingMore && payments.length > 0 && !hasMore}
            />
        </PageShell>
    );
}
